import jsPDF from 'jspdf';

/**
 * Genera y descarga (o devuelve) el PDF de una constancia.
 *
 * @param {object} opts
 * @param {string}  opts.redaccionHtml      - HTML ya renderizado (tokens reemplazados)
 * @param {string}  opts.nombreConstancia   - Nombre para el nombre de archivo
 * @param {string}  opts.claveConstancia    - Clave corta para el nombre del archivo
 * @param {string}  [opts.nombreEstudiante] - Nombre del estudiante (pie de página)
 * @param {string}  [opts.matricula]        - Matrícula del estudiante (pie de página)
 * @param {boolean} [opts.descargar=true]   - Si true descarga; si false devuelve dataURL
 * @returns {Promise<string|void>}
 */
export async function generateConstanciaPDF({
  redaccionHtml,
  nombreConstancia,
  claveConstancia,
  descargar = true,
}) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const maxWidth = pageW - margin * 2;
  const lineHeight = 6.2;

  let y = margin;

  const ensureSpace = (linesNeeded = 1) => {
    const required = linesNeeded * lineHeight;
    if (y + required > pageH - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const getTextWidth = (text, bold, fontSize) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(fontSize);
    return doc.getTextWidth(text || '');
  };

  const pushRun = (runs, text, bold) => {
    if (!text) return;
    const prev = runs[runs.length - 1];
    if (prev && prev.bold === bold) {
      prev.text += text;
    } else {
      runs.push({ text, bold });
    }
  };

  const collectRuns = (node, runs, bold = false) => {
    if (!node) return;

    if (node.nodeType === Node.TEXT_NODE) {
      pushRun(runs, (node.textContent || '').replace(/\u00a0/g, ' '), bold);
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tag = (node.tagName || '').toLowerCase();
    if (tag === 'br') {
      pushRun(runs, '\n', bold);
      return;
    }

    const nextBold = bold || tag === 'strong' || tag === 'b';
    const children = Array.from(node.childNodes || []);
    for (const child of children) {
      collectRuns(child, runs, nextBold);
    }
  };

  const tokenizeRuns = (runs) => {
    const tokens = [];
    for (const run of runs) {
      const parts = String(run.text || '').split(/(\n|\s+)/);
      for (const part of parts) {
        if (!part) continue;
        if (part === '\n') {
          tokens.push({ type: 'break' });
        } else if (/^\s+$/.test(part)) {
          tokens.push({ type: 'space', text: ' ', bold: run.bold });
        } else {
          tokens.push({ type: 'word', text: part, bold: run.bold });
        }
      }
    }
    return tokens;
  };

  const breakLongWord = (wordToken, fontSize) => {
    const pieces = [];
    let current = '';
    for (const ch of wordToken.text) {
      const candidate = current + ch;
      if (getTextWidth(candidate, wordToken.bold, fontSize) <= maxWidth || !current) {
        current = candidate;
      } else {
        pieces.push({ type: 'word', text: current, bold: wordToken.bold });
        current = ch;
      }
    }
    if (current) pieces.push({ type: 'word', text: current, bold: wordToken.bold });
    return pieces;
  };

  const makeLines = (runs, fontSize) => {
    const tokens = tokenizeRuns(runs);
    const lines = [];
    let lineTokens = [];
    let lineWidth = 0;
    let lastWasSpace = false;

    const flushLine = () => {
      while (lineTokens.length && lineTokens[lineTokens.length - 1].type === 'space') {
        const t = lineTokens.pop();
        lineWidth -= t.width;
      }
      lines.push({ tokens: lineTokens, width: Math.max(0, lineWidth) });
      lineTokens = [];
      lineWidth = 0;
      lastWasSpace = false;
    };

    for (const token of tokens) {
      if (token.type === 'break') {
        flushLine();
        continue;
      }

      if (token.type === 'space') {
        if (!lineTokens.length || lastWasSpace) continue;
        const width = getTextWidth(token.text, token.bold, fontSize);
        lineTokens.push({ ...token, width });
        lineWidth += width;
        lastWasSpace = true;
        continue;
      }

      const width = getTextWidth(token.text, token.bold, fontSize);
      if (width > maxWidth) {
        const chunks = breakLongWord(token, fontSize);
        for (const chunk of chunks) {
          const cw = getTextWidth(chunk.text, chunk.bold, fontSize);
          if (lineTokens.length && lineWidth + cw > maxWidth) {
            flushLine();
          }
          lineTokens.push({ ...chunk, width: cw });
          lineWidth += cw;
          lastWasSpace = false;
        }
        continue;
      }

      if (lineTokens.length && lineWidth + width > maxWidth) {
        flushLine();
      }
      lineTokens.push({ ...token, width });
      lineWidth += width;
      lastWasSpace = false;
    }

    if (lineTokens.length || lines.length === 0) {
      flushLine();
    }

    return lines;
  };

  const drawLine = (line, align, isLast, fontSize) => {
    const hasSpaces = line.tokens.some((t) => t.type === 'space');
    let x = margin;
    let extraPerSpace = 0;

    if (align === 'center') {
      x = margin + (maxWidth - line.width) / 2;
    } else if (align === 'right') {
      x = pageW - margin - line.width;
    } else if (align === 'justify' && !isLast && hasSpaces) {
      const spaceCount = line.tokens.filter((t) => t.type === 'space').length;
      if (spaceCount > 0 && line.width < maxWidth) {
        extraPerSpace = (maxWidth - line.width) / spaceCount;
      }
    }

    for (const token of line.tokens) {
      if (token.type === 'space') {
        x += token.width + extraPerSpace;
        continue;
      }
      doc.setFont('helvetica', token.bold ? 'bold' : 'normal');
      doc.setFontSize(fontSize);
      doc.text(token.text, x, y);
      x += token.width;
    }
  };

  const writeRunsParagraph = ({ runs, align = 'left', fontSize = 12, spaceAfter = 2.2 }) => {
    const hasContent = runs.some((r) => (r.text || '').replace(/\s+/g, '') !== '');
    if (!hasContent) {
      y += lineHeight * 0.75;
      return;
    }

    const lines = makeLines(runs, fontSize);
    ensureSpace(lines.length + 1);

    lines.forEach((line, idx) => {
      drawLine(line, align, idx === lines.length - 1, fontSize);
      y += lineHeight;
      if (y > pageH - margin) {
        doc.addPage();
        y = margin;
      }
    });

    y += spaceAfter;
  };

  const html = redaccionHtml || '';
  const container = document.createElement('div');
  container.innerHTML = html;

  const paragraphs = Array.from(container.querySelectorAll('p'));
  if (paragraphs.length === 0) {
    const fallbackRuns = [];
    collectRuns(container, fallbackRuns, false);
    writeRunsParagraph({ runs: fallbackRuns, align: 'left' });
  } else {
    for (const p of paragraphs) {
      const runs = [];
      collectRuns(p, runs, false);
      const className = p.className || '';
      const align =
        className.includes('ql-align-center') ? 'center' :
        className.includes('ql-align-right') ? 'right' :
        className.includes('ql-align-justify') ? 'justify' :
        'left';

      writeRunsParagraph({ runs, align });
    }
  }

  // Firma por default al final
  y += 12;
  writeRunsParagraph({ runs: [{ text: 'ATENTAMENTE', bold: true }], align: 'center', fontSize: 12, spaceAfter: 18 });
  writeRunsParagraph({ runs: [{ text: 'LIC. NYDIA ROCIO ACOSTA CERDA', bold: true }], align: 'center', fontSize: 12, spaceAfter: 0.5 });
  writeRunsParagraph({ runs: [{ text: 'Directora de Control Escolar', bold: true }], align: 'center', fontSize: 12, spaceAfter: 0 });

  const fileName = `Constancia_${(claveConstancia || 'doc').replace(/\s+/g, '_')}.pdf`;
  if (descargar) {
    doc.save(fileName);
  } else {
    return doc.output('datauristring');
  }
}
